// src/slm/scenarioActionPrompt.js
// Focused, single-purpose follow-up call for scenari_futuri mode, deciding
// whether to fork the scenario tree (and where) and whether a card helps.
//
// DESIGN NOTE — why this call never asks the model a yes/no question:
// the first version of this module asked exactly that ("biforca":
// true|false, "usa_carta": true|false) wrapped in a list of "NON biforcare
// se..." conditions, and in practice a local 7B answered false essentially
// every turn no matter how the prose was tuned (five rounds of it). That's
// the predictable outcome: abstention is a JUDGMENT task, which small
// models are bad at, and "false" is always the cheapest, safest token —
// especially when most of the prompt is prohibitions. Generation, by
// contrast, is what these models are reliably good at.
//
// So the model is now asked ONLY to generate a proposal, every single
// turn, and is explicitly told a separate system decides whether to use it
// (which removes any incentive to self-censor). Whether the proposal is
// actually applied is decided by decideScenarioAction() below — plain
// deterministic code, where the rules are explicit, cheap, and unit
// testable, instead of hidden inside a 7B's mood.
import { extractAndParseJSON } from "./jsonRepair";
import { render, renderStr, vocab } from "./prompts";

// The tree text's `[id] "label"` / "(in attesa)" / root "Oggi" markers are
// internal tokens (parsed back by extractValidNodeIds/readScenarioTreeState
// and the backend) and stay Italian in every language.
export function buildScenarioActionMessages({ chat, scenarioTree, cardLabels = [], likelyBranchId = null, lang = "it" }) {
  const v = vocab(lang);
  const treeText = scenarioTree && scenarioTree.trim() ? scenarioTree.trim() : v.empty_tree;
  const cardsText = cardLabels.length ? cardLabels.slice(0, 25).join(", ") : v.none_available;
  const hintLine = likelyBranchId
    ? renderStr("scenario_action_hint", lang, { branch_id: likelyBranchId })
    : "";

  const system = render("scenario_action_system", lang);

  const user = renderStr("scenario_action", lang, { tree: treeText, hint: hintLine, chat, cards: cardsText });

  return { messages: [{ role: "system", content: system }, { role: "user", content: user }] };
}

export const CARD_RELEVANCE = ["alta", "media", "bassa"];

// Grammar constraint (WebLLM's response_format.schema, and the engine
// addon's own msg.schema — engine/host/main.py passes it straight to
// llama.cpp). Every field is required: this call has no "decline" branch
// by design, so a response missing a field is a malformed response, not a
// meaningful "no". enum-constrains node_id/carta so the model can't invent
// a node id or a card name in the first place.
export function buildScenarioActionSchema({ validNodeIds, cardLabels = [] } = {}) {
  const nodeIdSchema = validNodeIds && validNodeIds.size
    ? { type: "string", enum: Array.from(validNodeIds) }
    : { type: "string" };
  const cardSchema = cardLabels.length
    ? { type: "string", enum: cardLabels }
    : { type: "string" };
  return JSON.stringify({
    type: "object",
    properties: {
      scenari: { type: "array", items: { type: "string" }, minItems: 2, maxItems: 4 },
      node_id: nodeIdSchema,
      carta_piu_utile: cardSchema,
      rilevanza_carta: { type: "string", enum: CARD_RELEVANCE },
    },
    required: ["scenari", "node_id", "carta_piu_utile", "rilevanza_carta"],
  });
}

// Every [id] token in the tree summary text, plus "root" — the full set of
// node ids the model is allowed to target (mirrors how the tree itself is
// rendered, see scenarioTreeSummary.js's `[${nodeId}]` line format).
export function extractValidNodeIds(scenarioTree) {
  const ids = new Set(["root"]);
  for (const m of (scenarioTree || "").matchAll(/\[([^\]]+)\]/g)) ids.add(m[1]);
  return ids;
}

// Deterministic state read straight off the rendered tree summary — no new
// plumbing needed, the string already carries everything the gate wants
// (see scenarioTreeSummary.js: `- [id] "label"` plus an "(in attesa)"
// suffix on an unconfirmed split).
export function readScenarioTreeState(scenarioTree) {
  const text = scenarioTree || "";
  return {
    hasPendingSplit: /\(in attesa\)/i.test(text),
    existingLabels: [...text.matchAll(/"([^"]+)"/g)].map((m) => m[1]),
  };
}

const norm = (s) => (s || "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();

// Parses the model's proposal. Never throws. `reason` distinguishes "the
// response didn't parse/validate" from a clean parse — with the yes/no
// fields gone, there is no longer any such thing as a valid "the model
// said no", so an empty result here always means something went wrong.
export function parseScenarioProposal(raw, { validNodeIds, cardLabels = [] }) {
  if ((raw || "").indexOf("{") === -1) return { proposal: null, reason: "no_json_found" };
  const parsed = extractAndParseJSON(raw);
  if (!parsed) return { proposal: null, reason: "invalid_json" };

  const branches = (Array.isArray(parsed.scenari) ? parsed.scenari : [])
    .filter((b) => typeof b === "string" && b.trim())
    .map((b) => b.trim())
    .slice(0, 4);
  if (branches.length < 2) return { proposal: null, reason: "missing_scenarios" };

  const nodeId = typeof parsed.node_id === "string" ? parsed.node_id.trim() : "";
  // An invented node id is recoverable: the scenarios are still usable,
  // they just graft at the root instead of the branch the model meant.
  // Dropping the whole turn over it would be strictly worse.
  const afterNodeId = validNodeIds.has(nodeId) ? nodeId : "root";

  const cardRaw = typeof parsed.carta_piu_utile === "string" ? parsed.carta_piu_utile.trim() : "";
  const card = cardLabels.includes(cardRaw) ? cardRaw : null;
  const relevance = CARD_RELEVANCE.includes(parsed.rilevanza_carta) ? parsed.rilevanza_carta : "bassa";

  return {
    proposal: { afterNodeId, branches, card, relevance },
    reason: "ok",
  };
}

// The "whether" half, deliberately in code rather than in the prompt.
// Returns { fork, card, skipped } — `skipped` names which rule (if any)
// held the proposal back, for the caller's debug logging.
export function decideScenarioAction(proposal, { treeState, usedCardLabels = [], minTurnsForFork = 0, userTurns = 0 } = {}) {
  if (!proposal) return { fork: null, card: null, skipped: "no_proposal" };
  const { hasPendingSplit, existingLabels = [] } = treeState || {};

  let fork = { afterNodeId: proposal.afterNodeId, branches: proposal.branches };
  let skipped = null;

  if (hasPendingSplit) {
    // One unresolved proposal at a time — a second one while the user
    // hasn't accepted/dismissed the first just stacks up undismissable UI.
    fork = null;
    skipped = "pending_split";
  } else if (userTurns < minTurnsForFork) {
    fork = null;
    skipped = "too_early";
  } else {
    const known = new Set(existingLabels.map(norm));
    if (proposal.branches.every((b) => known.has(norm(b)))) {
      // Every proposed direction already exists in the tree — this is the
      // model restating the current state rather than projecting forward.
      fork = null;
      skipped = "duplicate_branches";
    }
  }

  // A card already sitting on the table is not a fresh prompt for thought.
  const alreadyOnTable = proposal.card && usedCardLabels.some((l) => norm(l) === norm(proposal.card));
  // "alta" is the normal bar. The exception exists because a relevance
  // threshold is still something a cautious model can duck under forever —
  // the same abstention trap this module was rewritten to escape, just in a
  // subtler costume. So the FIRST card of a session also gets in at "media":
  // the mechanic proves itself visibly early (which is exactly what was
  // missing — a user who never once sees a card fly in reasonably concludes
  // the feature is broken), then the bar tightens for the rest of the
  // session. Can't spam either way: at most one card per turn, and
  // alreadyOnTable stops repeats.
  const boardIsEmpty = usedCardLabels.length === 0;
  const relevantEnough = proposal.relevance === "alta" || (boardIsEmpty && proposal.relevance === "media");
  const card = proposal.card && relevantEnough && !alreadyOnTable ? proposal.card : null;

  return { fork, card, skipped };
}
